import {
  DashboardTileNode,
  VALID_CHART_TYPES,
  VALID_NODE_TYPES,
} from "./dashboard-tile-node";
import { Tile, VALID_GRID_AREAS } from "./tile";
import { TimePeriod, VALID_TIME_UNITS } from "./time-period";

export class TileStructureParser {
  private tiles: Tile[];

  constructor(jsonString: any, parseJson = true) {
    let json;
    if (parseJson) {
      json = JSON.parse(jsonString);
    } else {
      json = jsonString;
    }
    this.tiles = this.parseTiles(json);
  }

  private parseTiles(json: any): Tile[] {
    if (!Array.isArray(json.tiles)) {
      throw new Error('Invalid JSON structure: "tiles" must be an array.');
    }

    return json.tiles.map((tileJson: any) => {
      // Validate and parse each tile entry
      const {
        id,
        title,
        position,
        grid_area,
        frequency,
        time_period,
        nodes,
        use_aggregated_for_single_node,
      } = tileJson;

      const normalizedFrequency =
        frequency === "five_minutes" ? "quarterhourly" : frequency;

      if (typeof id !== "string") {
        throw new Error('Invalid tile: "uuid" must be a string.');
      }
      if (typeof title !== "string") {
        throw new Error('Invalid tile: "title" must be a string.');
      }
      if (typeof position !== "number" || position < 0) {
        throw new Error(
          'Invalid tile: "position" must be a non-negative number.',
        );
      }
      if (!VALID_GRID_AREAS.includes(grid_area)) {
        throw new Error('Invalid tile: "grid_area" is not valid.');
      }

      const timePeriod = this.parseTimePeriod(time_period);
      const nodesArray = this.parseNodes(nodes);

      return new Tile(
        id,
        title,
        position,
        grid_area,
        normalizedFrequency,
        timePeriod,
        nodesArray,
        use_aggregated_for_single_node || false,
      );
    });
  }

  private parseTimePeriod(timePeriodJson: any): TimePeriod {
    const { amount, time_unit } = timePeriodJson;
    if (typeof amount !== "number" || amount <= 0) {
      throw new Error(
        'Invalid time_period: "amount" must be greater than zero.',
      );
    }
    if (!VALID_TIME_UNITS.includes(time_unit)) {
      throw new Error('Invalid time_period: "time_unit" is not valid.');
    }
    return new TimePeriod(amount, time_unit);
  }

  private parseNodes(nodesJson: any[]): DashboardTileNode[] {
    // TODO: not sure if we want to enforce this!
    // TODO: because we should check every single tile for nodes before saving/transfering and in case we should inhibit the action
    // TODO: and throw a warning telling "you are trying to save tiles without nodes"
    /* if (!Array.isArray(nodesJson) || nodesJson.length === 0) {
      throw new Error('Invalid nodes: "nodes" must be a non-empty array.');
    } */

    return nodesJson.map((nodeJson: any) => {
      const { id, name, chart_type, type } = nodeJson;
      if (typeof id !== "string") {
        throw new Error('Invalid node: "id" must be a string.');
      }
      if (typeof name !== "string") {
        throw new Error('Invalid node: "name" must be a string.');
      }
      if (!VALID_CHART_TYPES.includes(chart_type)) {
        throw new Error('Invalid node: "chart_type" is not valid.');
      }
      if (!VALID_NODE_TYPES.includes(type)) {
        throw new Error('Invalid node: "type" is not valid.');
      }
      return new DashboardTileNode(id, name, chart_type, type);
    });
  }

  getTiles(): Tile[] {
    return this.tiles;
  }
}
